// src/yomiri/download/DownloadQueueManager.ts
import type { YomiriManga, YomiriChapter, YomiriPage } from '@/yomiri/types/extension.ts';
import type { AnimeTitle, AnimeEpisode, AnimeServer } from '@/yomiri/anime/types.ts';
import type { DownloadJob, DownloadJobStatus, DownloadRealProgress } from '@/yomiri/types/work.ts';
import { createDownloadJob } from '@/yomiri/models/Work.ts';
import { downloadLocalChapter, getLocalChapter, localChapterPages } from '@/yomiri/storage/LocalChapters.ts';
import { listAnimeDownloads, saveAnimeEpisode } from '@/yomiri/anime/AnimeDownloads.ts';
import { yomiriDatabase } from '@/yomiri/storage/YomiriDatabase.ts';

export type DownloadStatus = 'QUEUED' | 'DOWNLOADING' | 'PAUSED' | 'ERROR' | 'COMPLETED';

export interface QueuedDownload {
    id: string; // `${sourceId}_${chapterId}` or job id
    type?: 'manga' | 'anime';
    manga?: YomiriManga;
    chapter?: YomiriChapter;
    anime?: AnimeTitle;
    episode?: AnimeEpisode;
    server?: AnimeServer;
    sourceName: string;
    status: DownloadStatus;
    progress: number; // 0 to 100
    pagesDownloaded: number;
    totalPages: number;
    bytesDownloaded?: number;
    totalBytes?: number;
    speedBps?: number;
    etaSeconds?: number;
    error?: string;
    enqueuedAt: number;
    jobId?: string;
}

class DownloadQueueManager {
    private queue: QueuedDownload[] = [];
    private isProcessing = false;
    private isPaused = false;
    private currentAbortController: AbortController | null = null;
    private currentItemId: string | null = null;

    constructor() {
        if (typeof window !== 'undefined') {
            void this.syncFromDatabase();
        }
    }

    /**
     * Sincroniza y restaura trabajos de descarga registrados previamente en YomiriDatabase.
     */
    async syncFromDatabase(): Promise<void> {
        try {
            const dbJobs = await yomiriDatabase.getAllDownloadJobs();
            for (const job of dbJobs) {
                if (['pending', 'queued', 'downloading'].includes(job.status)) {
                    // Si quedó un trabajo pendiente, marcarlo para reanudación segura
                    const existing = this.queue.find(q => q.jobId === job.id || q.id === job.id);
                    if (!existing) {
                        this.queue.push({
                            id: job.id,
                            sourceName: job.editionId?.split('::')[0] || 'Descargas',
                            status: 'QUEUED',
                            progress: job.realProgress?.percent || 0,
                            pagesDownloaded: job.realProgress?.currentItem || 0,
                            totalPages: job.realProgress?.totalItems || 0,
                            bytesDownloaded: job.realProgress?.downloadedBytes || 0,
                            totalBytes: job.realProgress?.totalBytes || 0,
                            speedBps: 0,
                            etaSeconds: 0,
                            enqueuedAt: Date.parse(job.createdAt) || Date.now(),
                            jobId: job.id,
                        });
                    }
                }
            }
            this.emitChange();
        } catch {
            // Ignorar fallos de IndexedDB en entornos sin soporte
        }
    }

    getQueue(): QueuedDownload[] {
        return [...this.queue];
    }

    isQueueActive(): boolean {
        return this.isProcessing && !this.isPaused;
    }

    /**
     * Encola la descarga de un capítulo de manga con métricas de DownloadJob.
     */
    enqueue(manga: YomiriManga, chapter: YomiriChapter, sourceName = 'Fuente'): void {
        const id = `${chapter.sourceId}_${chapter.id}`;
        const existing = this.queue.find(item => item.id === id);

        if (existing && ['QUEUED', 'DOWNLOADING', 'PAUSED'].includes(existing.status)) {
            console.log(`[DownloadQueue] El capítulo ${chapter.name} ya está en la cola`);
            return;
        }

        const jobId = `dl_${chapter.sourceId}_${chapter.id}_${Date.now()}`;
        this.queue = this.queue.filter(item => item.id !== id);

        const item: QueuedDownload = {
            id,
            type: 'manga',
            manga,
            chapter,
            sourceName: sourceName || manga.sourceId,
            status: 'QUEUED',
            progress: 0,
            pagesDownloaded: 0,
            totalPages: 0,
            bytesDownloaded: 0,
            totalBytes: 0,
            speedBps: 0,
            etaSeconds: 0,
            enqueuedAt: Date.now(),
            jobId,
        };

        this.queue.push(item);

        // Crear y persistir el DownloadJob en YomiriDatabase
        const downloadJob: DownloadJob = createDownloadJob({
            id: jobId,
            workId: manga.id,
            editionId: `${manga.sourceId}::${manga.id}`,
            itemId: chapter.id,
            itemTitle: chapter.name,
            destination: 'indexeddb',
            status: 'pending',
            realProgress: {
                totalBytes: 0,
                downloadedBytes: 0,
                percent: 0,
                speedBps: 0,
                etaSeconds: 0,
                currentItem: 0,
                totalItems: 0,
            },
        });

        void yomiriDatabase.saveDownloadJob(downloadJob).catch(() => {});

        this.emitChange();
        void this.processNext();
    }

    /**
     * Encola la descarga de un episodio de anime con archivo directo.
     */
    enqueueAnime(anime: AnimeTitle, episode: AnimeEpisode, server: AnimeServer): void {
        const id = `anime_${anime.sourceId}_${episode.id}`;
        const existing = this.queue.find(item => item.id === id);

        if (existing && ['QUEUED', 'DOWNLOADING', 'PAUSED'].includes(existing.status)) {
            return;
        }

        const jobId = `dl_anime_${anime.sourceId}_${episode.id}_${Date.now()}`;
        this.queue = this.queue.filter(item => item.id !== id);

        const item: QueuedDownload = {
            id,
            type: 'anime',
            anime,
            episode,
            server,
            sourceName: server.name || anime.sourceId,
            status: 'QUEUED',
            progress: 0,
            pagesDownloaded: 0,
            totalPages: 1,
            bytesDownloaded: 0,
            totalBytes: 0,
            speedBps: 0,
            etaSeconds: 0,
            enqueuedAt: Date.now(),
            jobId,
        };

        this.queue.push(item);

        const downloadJob: DownloadJob = createDownloadJob({
            id: jobId,
            workId: anime.id,
            editionId: `${anime.sourceId}::${anime.id}`,
            itemId: episode.id,
            itemTitle: episode.name,
            destination: 'filesystem',
            status: 'pending',
            realProgress: {
                totalBytes: 0,
                downloadedBytes: 0,
                percent: 0,
                speedBps: 0,
                etaSeconds: 0,
                currentItem: 0,
                totalItems: 1,
            },
        });

        void yomiriDatabase.saveDownloadJob(downloadJob).catch(() => {});

        this.emitChange();
        void this.processNext();
    }

    /**
     * Pausa una descarga específica o toda la cola de descargas activa.
     */
    pause(id?: string): void {
        if (id) {
            const item = this.queue.find(q => q.id === id);
            if (item) {
                item.status = 'PAUSED';
                if (item.jobId) {
                    void yomiriDatabase.updateDownloadJobProgress(item.jobId, {}, 'paused').catch(() => {});
                }
                if (this.currentItemId === id && this.currentAbortController) {
                    this.currentAbortController.abort('Descarga pausada por el usuario');
                    this.currentAbortController = null;
                    this.isProcessing = false;
                }
            }
        } else {
            this.isPaused = true;
            this.currentAbortController?.abort('Descargas pausadas');
            this.currentAbortController = null;
            this.isProcessing = false;
            for (const item of this.queue) {
                if (item.status === 'DOWNLOADING') {
                    item.status = 'PAUSED';
                    if (item.jobId) {
                        void yomiriDatabase.updateDownloadJobProgress(item.jobId, {}, 'paused').catch(() => {});
                    }
                }
            }
        }
        this.emitChange();
    }

    /**
     * Reanuda descargas pausadas.
     */
    resume(id?: string): void {
        if (id) {
            const item = this.queue.find(q => q.id === id);
            if (item && item.status === 'PAUSED') {
                item.status = 'QUEUED';
                if (item.jobId) {
                    void yomiriDatabase.updateDownloadJobProgress(item.jobId, {}, 'pending').catch(() => {});
                }
            }
        } else {
            this.isPaused = false;
            for (const item of this.queue) {
                if (item.status === 'PAUSED') {
                    item.status = 'QUEUED';
                    if (item.jobId) {
                        void yomiriDatabase.updateDownloadJobProgress(item.jobId, {}, 'pending').catch(() => {});
                    }
                }
            }
        }
        this.emitChange();
        void this.processNext();
    }

    /**
     * Cancela y remueve una descarga de la cola.
     */
    cancel(id: string): void {
        const index = this.queue.findIndex(item => item.id === id);
        if (index === -1) return;

        const item = this.queue[index];
        if (item.status === 'DOWNLOADING' || this.currentItemId === id) {
            this.currentAbortController?.abort('Cancelado por el usuario');
            this.currentAbortController = null;
            this.isProcessing = false;
        }

        if (item.jobId) {
            void yomiriDatabase.deleteDownloadJob(item.jobId).catch(() => {});
        }

        this.queue.splice(index, 1);
        this.emitChange();
    }

    /**
     * Reintenta una descarga fallida o cancelada.
     */
    retry(id: string): void {
        const item = this.queue.find(q => q.id === id);
        if (!item) return;

        item.status = 'QUEUED';
        item.error = undefined;
        item.progress = 0;
        item.bytesDownloaded = 0;
        item.speedBps = 0;
        item.etaSeconds = 0;

        if (item.jobId) {
            void yomiriDatabase.updateDownloadJobProgress(item.jobId, {
                downloadedBytes: 0,
                percent: 0,
                speedBps: 0,
                etaSeconds: 0,
            }, 'pending').catch(() => {});
        }

        this.emitChange();
        void this.processNext();
    }

    clearCompleted(): void {
        this.queue = this.queue.filter(item => item.status !== 'COMPLETED');
        this.emitChange();
    }

    /**
     * Comprueba si un recurso (capítulo o episodio) está descargado localmente para modo offline.
     */
    async isDownloaded(type: 'manga' | 'anime', sourceId: string, itemId: string): Promise<boolean> {
        try {
            if (type === 'manga') {
                const chapter = await getLocalChapter(sourceId, itemId);
                return !!chapter;
            } else {
                const downloads = await listAnimeDownloads();
                const key = JSON.stringify([sourceId, itemId]);
                return downloads.some(d => d.key === key);
            }
        } catch {
            return false;
        }
    }

    /**
     * Recupera páginas locales offline para un capítulo de manga descargado.
     */
    async getOfflinePages(sourceId: string, chapterId: string): Promise<YomiriPage[] | undefined> {
        try {
            return await localChapterPages(sourceId, chapterId);
        } catch {
            return undefined;
        }
    }

    private emitChange(): void {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('yomiri-download-queue-changed', {
                detail: { queue: this.getQueue() }
            }));
        }
    }

    private async processNext(): Promise<void> {
        if (this.isProcessing || this.isPaused) return;

        const next = this.queue.find(item => item.status === 'QUEUED');
        if (!next) return;

        this.isProcessing = true;
        this.currentItemId = next.id;
        next.status = 'DOWNLOADING';
        this.emitChange();

        if (next.jobId) {
            void yomiriDatabase.updateDownloadJobProgress(next.jobId, {}, 'downloading').catch(() => {});
        }

        this.currentAbortController = new AbortController();
        const signal = this.currentAbortController.signal;

        try {
            if (next.type === 'anime' && next.anime && next.episode && next.server) {
                await this.downloadAnimeEpisode(next, signal);
            } else if (next.manga && next.chapter) {
                await this.downloadChapter(next, signal);
            } else {
                throw new Error('Datos incompletos para procesar la descarga.');
            }

            next.status = 'COMPLETED';
            next.progress = 100;
            next.speedBps = 0;
            next.etaSeconds = 0;

            if (next.jobId) {
                void yomiriDatabase.updateDownloadJobProgress(next.jobId, {
                    percent: 100,
                    downloadedBytes: next.totalBytes || next.bytesDownloaded || 0,
                    speedBps: 0,
                    etaSeconds: 0,
                }, 'completed').catch(() => {});
            }
        } catch (error: any) {
            if (signal.aborted) {
                const title = next.chapter?.name || next.episode?.name || next.id;
                console.log(`[DownloadQueue] Descarga cancelada o pausada: ${title}`);
                if (next.status !== 'PAUSED') {
                    next.status = 'PAUSED';
                }
            } else {
                const title = next.chapter?.name || next.episode?.name || next.id;
                console.error(`[DownloadQueue] Error en ${title}:`, error);
                next.status = 'ERROR';
                next.error = error?.message || 'Error al procesar descarga';

                if (next.jobId) {
                    void yomiriDatabase.updateDownloadJobProgress(next.jobId, {}, 'failed').catch(() => {});
                }
            }
        } finally {
            this.isProcessing = false;
            this.currentItemId = null;
            this.currentAbortController = null;
            this.emitChange();

            // Continuar con el siguiente en la cola si no está pausada
            if (!this.isPaused) {
                setTimeout(() => void this.processNext(), 300);
            }
        }
    }

    private async downloadChapter(item: QueuedDownload, signal: AbortSignal): Promise<void> {
        const { manga, chapter } = item;
        if (!manga || !chapter) return;

        const sourceProvider = (await import('@/yomiri/core/SourceProvider.ts')).sourceProvider;

        // Comprobación de modo offline existente
        if (await getLocalChapter(chapter.sourceId, chapter.id)) {
            item.progress = 100;
            return;
        }

        const pages: YomiriPage[] = await sourceProvider.getPages(chapter.sourceId, chapter.id);
        item.totalPages = pages.length;

        let lastTime = Date.now();
        let lastPages = 0;
        const estimatedPageBytes = 400 * 1024; // 400 KB promedio por página estimada
        const totalEstBytes = pages.length * estimatedPageBytes;
        item.totalBytes = totalEstBytes;

        await downloadLocalChapter(manga, chapter, pages, signal, (done, total) => {
            const now = Date.now();
            const elapsedSec = Math.max(0.1, (now - lastTime) / 1000);
            const deltaPages = done - lastPages;

            const speedPages = deltaPages / elapsedSec;
            const speedBps = Math.round(speedPages * estimatedPageBytes);
            const remainingPages = Math.max(0, total - done);
            const etaSeconds = speedPages > 0 ? Math.round(remainingPages / speedPages) : undefined;

            lastTime = now;
            lastPages = done;

            item.pagesDownloaded = done;
            item.totalPages = total;
            item.progress = total ? Math.round((done / total) * 100) : 0;
            item.bytesDownloaded = done * estimatedPageBytes;
            item.speedBps = speedBps;
            item.etaSeconds = etaSeconds;

            if (item.jobId) {
                void yomiriDatabase.updateDownloadJobProgress(item.jobId, {
                    currentItem: done,
                    totalItems: total,
                    percent: item.progress,
                    downloadedBytes: item.bytesDownloaded,
                    totalBytes: totalEstBytes,
                    speedBps,
                    etaSeconds,
                }, 'downloading').catch(() => {});
            }

            this.emitChange();
        });

        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('yomiri-downloads-changed'));
        }
    }

    private async downloadAnimeEpisode(item: QueuedDownload, signal: AbortSignal): Promise<void> {
        const { anime, episode, server } = item;
        if (!anime || !episode || !server) return;

        let lastTime = Date.now();
        let lastBytes = 0;

        await saveAnimeEpisode(anime, episode, server, signal, (bytes, total) => {
            const now = Date.now();
            const elapsedSec = Math.max(0.1, (now - lastTime) / 1000);
            const deltaBytes = bytes - lastBytes;
            const speedBps = Math.round(deltaBytes / elapsedSec);
            const remainingBytes = Math.max(0, total - bytes);
            const etaSeconds = speedBps > 0 ? Math.round(remainingBytes / speedBps) : undefined;

            lastTime = now;
            lastBytes = bytes;

            item.bytesDownloaded = bytes;
            item.totalBytes = total;
            item.progress = total ? Math.round((bytes / total) * 100) : 0;
            item.speedBps = speedBps;
            item.etaSeconds = etaSeconds;

            if (item.jobId) {
                void yomiriDatabase.updateDownloadJobProgress(item.jobId, {
                    downloadedBytes: bytes,
                    totalBytes: total,
                    percent: item.progress,
                    speedBps,
                    etaSeconds,
                    currentItem: 1,
                    totalItems: 1,
                }, 'downloading').catch(() => {});
            }

            this.emitChange();
        });
    }
}

export const downloadQueueManager = new DownloadQueueManager();
